import { Service } from "src/common/models/service"
import { ServiceError } from "../state/types"
import { Alert, Button, Checkbox, Divider, Form, Input, List, Row, Select, Skeleton, Space, UploadFile, UploadProps, GetProp, Upload, Modal } from "antd"
import { useSelector } from "react-redux"
import { RootState } from "src/state/reducer"
import { User } from "src/common/models/user"
import { FormActionButtons } from "src/components/FormActionButtons"
import { SystemDescriptions } from "src/common/descriptions"
import { Col12, Col24, Col6 } from "src/components/Columns"
import { getInputRequiredRule } from "src/common/util"
import { CurrencyInput } from "src/components/CurrencyInput"
import { ServiceCategory } from "src/common/models/serviceCategory"
import { useEffect, useState } from "react"
import { PlusOutlined } from '@ant-design/icons';
import * as Styles from './styles'
import { ScheduleDayKey, ServiceSchedule, WeeklyScheduleDays } from "src/common/models/schedule"

interface ReduxProps {
    error?: ServiceError
    user?: User
    serviceCategories?: ServiceCategory[]
}

interface Props {
    initialValues?: Service
    onFinish: (service: Partial<Service>) => void
    onCancel: () => void
}

export const ServiceForm = (props: Props) => {
    const [form] = Form.useForm();

    const scheduleDayOptions: { label: string, value: ScheduleDayKey }[] = [
        { label: "Lunes", value: "monday" },
        { label: "Martes", value: "tuesday" },
        { label: "Miercoles", value: "wednesday" },
        { label: "Jueves", value: "thursday" },
        { label: "Viernes", value: "friday" },
        { label: "Sabado", value: "saturday" },
        { label: "Domingo", value: "sunday" },
    ]

    const getSelectedScheduleDays = (schedule?: ServiceSchedule): ScheduleDayKey[] => (
        scheduleDayOptions
            .map(option => option.value)
            .filter(day => Boolean(schedule?.days?.[day]))
    )

    const buildSchedules = (days?: ScheduleDayKey[], note?: string): ServiceSchedule[] => {
        if (!days || days.length === 0) {
            return []
        }

        return [{
            type: "WEEKLY",
            days: scheduleDayOptions.reduce<WeeklyScheduleDays>((acc, option) => ({
                ...acc,
                [option.value]: days.includes(option.value),
            }), {}),
            note,
        }]
    }

    useEffect(() => {
        if (props.initialValues) {
            const initialSchedule = props.initialValues.schedules?.[0]
            form.setFieldsValue({
                name: props.initialValues.name,
                price: props.initialValues.price,
                isMonthly: props.initialValues.isMonthly,
                category: props.initialValues.serviceCategoryId,
                status: props.initialValues.isActive,
                externalId: props.initialValues.externalId,
                hasSchedule: Boolean(initialSchedule),
                scheduleDays: getSelectedScheduleDays(initialSchedule),
                scheduleNote: initialSchedule?.note,
            })

            setCharacteristics(props.initialValues.characteristics)
            setScheduleEnabled(Boolean(initialSchedule))

            if (props.initialValues.imageUrl) {
                setPreviewImage(props.initialValues?.imageUrl);

                setFileList([
                    {
                        uid: props.initialValues.externalId,
                        name: `${props.initialValues.externalId}.jpg`,
                        status: "done",
                        url: props.initialValues.imageUrl,
                    },
                ])
            }
        }
    }, [])


    const reduxProps: ReduxProps = useSelector((state: RootState) => ({
        error: state.service.error,
        user: state.security.user,
        serviceCategories: state.service.serviceCategories || [],
    }))

    const [characteristics, setCharacteristics] = useState<string[]>([])
    const [currentCharacteristic, setCurrentCharacteristic] = useState<string>('')
    const [fileList, setFileList] = useState<UploadFile[]>([])
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState('');
    const [hasImageChanged, setHasImageChanged] = useState<boolean>(false)
    const [scheduleEnabled, setScheduleEnabled] = useState<boolean>(false)

    const descriptions = SystemDescriptions.PAGES.SERVICE.CREATE
    const commonDescriptions = SystemDescriptions.PAGES.COMMON

    const getImageBase64 = async (): Promise<string | undefined> => {
        if (hasImageChanged) {
            return fileList.length > 0
                ? await getBase64(fileList[0].originFileObj as any)
                : ''
        }

        return undefined
    }

    const onFinish = async (formValues) => {
        const service: Service = {
            name: formValues.name,
            price: formValues.price,
            isMonthly: formValues.isMonthly,
            characteristics: characteristics,
            isActive: formValues.status,
            serviceCategoryId: formValues.category,
            userId: reduxProps.user?.id!,
            externalId: formValues.externalId,
            createdAt: '',
            updatedAt: '',
            imageUrl: (hasImageChanged && props.initialValues?.imageUrl) ? '' : props.initialValues?.imageUrl!,
            imageBase64: await getImageBase64(),
            schedules: scheduleEnabled ? buildSchedules(formValues.scheduleDays, formValues.scheduleNote) : [],
        }

        if (props.initialValues && props.initialValues.id) {
            service.id = props.initialValues.id
        }

        props.onFinish(service)
    }

    const getButtonStatus = (): boolean => {
        const requiredFields = ["name", "price", "isMonthly", "category", "status", "externalId"]
        const values = form.getFieldsValue(requiredFields)
        const hasMissingRequiredField = requiredFields.some(field => (
            values[field] === undefined ||
            values[field] === null ||
            values[field] === ""
        ))

        return props.initialValues
            ? false
            : hasMissingRequiredField ||
            form.getFieldsError().some(({ errors }) => errors.length > 0)
    }

    const handleCancelClick = () => {
        props.onCancel()
    }

    const renderButtons = () => (
        <FormActionButtons
            cancelText={descriptions.CANCEL_BUTTON}
            actionText={descriptions.SAVE_BUTTON}
            actionDisabled={getButtonStatus}
            onCancel={handleCancelClick}
        />
    )

    const removeCharacteristic = (indexToRemove: number) => {
        const currentCharacteristics = [...characteristics]

        if (indexToRemove >= 0 && indexToRemove < currentCharacteristics.length) {
            currentCharacteristics.splice(indexToRemove, 1);
        }

        setCharacteristics(currentCharacteristics)
    }

    const renderCharacteristics = () => {
        return <Col24>
            <>
                <Form.Item
                    label={descriptions.FORM.CHARACTERISTICS.LABEL}
                    name={"characteristic"}
                    validateTrigger="onBlur"
                >
                    <Space.Compact style={{ width: '100%' }}>
                        <Input
                            placeholder={descriptions.FORM.CHARACTERISTICS.PLACEHOLDER}
                            onChange={(e) => {
                                setCurrentCharacteristic(e.target.value)
                            }}
                            value={currentCharacteristic}
                        />
                        <Button
                            onClick={() => {
                                if (!currentCharacteristic || currentCharacteristic.length === 0) {
                                    return
                                }

                                setCharacteristics([
                                    ...characteristics,
                                    currentCharacteristic,
                                ])

                                setCurrentCharacteristic('')
                            }}
                            type="primary">
                            {descriptions.ADD}
                        </Button>
                    </Space.Compact>
                </Form.Item>
                <List
                    className="demo-loadmore-list"
                    itemLayout="horizontal"
                    dataSource={characteristics}
                    size="small"
                    renderItem={(item, idx) => (
                        <List.Item
                            actions={[
                                <a onClick={() => removeCharacteristic(idx)} key="list-loadmore-edit">
                                    {descriptions.REMOVE}
                                </a>,
                            ]}
                        >
                            <Skeleton avatar title={false} active loading={false}>
                                <div>{item}</div>
                            </Skeleton>
                        </List.Item>
                    )}
                    style={{
                        marginBottom: 24,
                    }}
                />
            </>
        </Col24>
    }

    type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];

    const getBase64 = (file: FileType): Promise<string> =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file as any);
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = (error) => reject(error);
        });

    const handlePreview = async (file) => {
        if (!file.url && !file.preview) {
            file.preview = await getBase64(file.originFileObj);
        }
        setPreviewImage(file.url || file.preview);
        setPreviewOpen(true);
    };

    const handleChange = ({ fileList: newFileList }) => {
        setFileList(newFileList);
    };

    const beforeUpload = (file) => {
        const allowedImageTypes: string[] = [
            "image/jpeg",
            "image/jpg",
        ]

        const isAllowed = allowedImageTypes.includes(file.type);
        if (!isAllowed) {
            alert('El formato de la imágen no es permitido');
        }

        setHasImageChanged(true)

        return isAllowed || Upload.LIST_IGNORE;
    };

    const renderFileSelector = () => {
        return <Col6>
            <Form.Item
                label={descriptions.FORM.IMAGE.LABEL}
            >
                <Styles.ImageSelectorContainer>
                    <Upload
                        listType="picture-card"
                        fileList={fileList}
                        onPreview={handlePreview}
                        onChange={handleChange}
                        beforeUpload={beforeUpload}
                        showUploadList={{ showRemoveIcon: true }}
                        customRequest={({ file, onSuccess }) => {
                            setTimeout(() => {
                                if (onSuccess)
                                    onSuccess("ok");
                            }, 0);
                        }}
                    >
                        {
                            (fileList.length >= 1)
                                ? null
                                : <div><PlusOutlined /><div style={{ marginTop: 8 }}>
                                    {descriptions.FORM.IMAGE.PLACEHOLDER}
                                </div></div>}
                    </Upload>
                    <Modal open={previewOpen} footer={null} onCancel={() => setPreviewOpen(false)}>
                        <img alt="preview" style={{ width: '100%' }} src={previewImage} />
                    </Modal>
                </Styles.ImageSelectorContainer>
            </Form.Item>
        </Col6>
    }

    const renderScheduleSection = () => (
        <Col24>
            <>
                <Divider orientation="left">Horario de propuesta</Divider>
                <Row gutter={[16, 8]}>
                    <Col24>
                        <Form.Item
                            name="hasSchedule"
                            valuePropName="checked"
                        >
                            <Checkbox
                                checked={scheduleEnabled}
                                onChange={(event) => {
                                    setScheduleEnabled(event.target.checked)
                                    if (!event.target.checked) {
                                        form.setFieldsValue({ scheduleDays: [], scheduleNote: undefined })
                                    }
                                }}
                            >
                                Este servicio tiene horario visible en propuestas
                            </Checkbox>
                        </Form.Item>
                    </Col24>
                    {
                        scheduleEnabled &&
                        <>
                            <Col12>
                                <Form.Item
                                    label="Dias"
                                    name="scheduleDays"
                                    rules={[
                                        {
                                            validator: (_, value) => {
                                                if (!scheduleEnabled || (value && value.length > 0)) {
                                                    return Promise.resolve()
                                                }

                                                return Promise.reject(new Error("Selecciona al menos un dia"))
                                            },
                                        },
                                    ]}
                                >
                                    <Checkbox.Group options={scheduleDayOptions} />
                                </Form.Item>
                            </Col12>
                            <Col12>
                                <Form.Item
                                    label="Nota"
                                    name="scheduleNote"
                                >
                                    <Input placeholder="Ej. El orden de los dias puede variar" />
                                </Form.Item>
                            </Col12>
                        </>
                    }
                </Row>
            </>
        </Col24>
    )

    const renderForm = () => (
        <Form
            form={form}
            layout="vertical"
            requiredMark={false}
            autoComplete={'none'}
            onFinish={onFinish}
        >
            <Row gutter={[16, 8]}>
                <Col6>
                    <Form.Item
                        label={descriptions.FORM.NAME.LABEL}
                        name={"name"}
                        rules={[
                            getInputRequiredRule("name"),
                        ]}
                        validateTrigger="onBlur"
                    >
                        <Input
                            placeholder={descriptions.FORM.NAME.PLACEHOLDER}
                        />
                    </Form.Item>
                </Col6>
                <Col6>
                    <Form.Item
                        label={descriptions.FORM.PRICE.LABEL}
                        name={"price"}
                        rules={[
                            getInputRequiredRule("price"),
                        ]}
                        validateTrigger="onBlur"
                    >
                        <CurrencyInput
                            placeholder={descriptions.FORM.PRICE.PLACEHOLDER}
                        />
                    </Form.Item>
                </Col6>
                <Col6>
                    <Form.Item
                        label={descriptions.FORM.IS_MONTHLY.LABEL}
                        name={"isMonthly"}
                        validateTrigger="onBlur"
                        rules={[
                            getInputRequiredRule("isMonthly"),
                        ]}
                    >
                        <Select
                            options={[
                                {
                                    value: 1,
                                    label: commonDescriptions.YES
                                },
                                {
                                    value: 0,
                                    label: commonDescriptions.NO,
                                },
                            ]}
                            placeholder={descriptions.FORM.IS_MONTHLY.PLACEHOLDER}
                        />
                    </Form.Item>
                </Col6>
                <Col6>
                    <Form.Item
                        label={descriptions.FORM.CATEGORY.LABEL}
                        name={"category"}
                        validateTrigger="onBlur"
                        rules={[
                            getInputRequiredRule("category"),
                        ]}
                    >
                        <Select
                            options={reduxProps.serviceCategories?.map(category => ({
                                value: category.id,
                                label: category.name,
                            }))}
                            placeholder={descriptions.FORM.CATEGORY.PLACEHOLDER}
                        />
                    </Form.Item>
                </Col6>
                <Col6>
                    <Form.Item
                        label={descriptions.FORM.IS_ACTIVE.LABEL}
                        name={"status"}
                        validateTrigger="onBlur"
                        rules={[
                            getInputRequiredRule("status"),
                        ]}
                    >
                        <Select
                            options={[
                                {
                                    value: 1,
                                    label: commonDescriptions.ACTIVE_STATUS
                                },
                                {
                                    value: 0,
                                    label: commonDescriptions.INACTIVE_STATUS,
                                },
                            ]}
                            placeholder={descriptions.FORM.IS_ACTIVE.PLACEHOLDER}
                        />
                    </Form.Item>
                </Col6>
                <Col6>
                    <Form.Item
                        label={descriptions.FORM.EXTERNAL_ID.LABEL}
                        name={"externalId"}
                        rules={[
                            getInputRequiredRule(descriptions.FORM.EXTERNAL_ID.LABEL),
                        ]}
                        validateTrigger="onBlur"
                    >
                        <Input
                            placeholder={descriptions.FORM.EXTERNAL_ID.PLACEHOLDER}
                        />
                    </Form.Item>
                </Col6>
                {renderFileSelector()}
                <Col24><hr /></Col24>
                {renderCharacteristics()}
                {renderScheduleSection()}
            </Row>
            {
                reduxProps.error &&
                <Alert
                    message={"Error"}
                    showIcon
                    description={reduxProps.error?.detail.message ?? commonDescriptions.FORM.UNKNOWN_ERROR}
                    type="error"
                    style={{
                        marginBottom: 12,
                    }}
                />
            }
            {renderButtons()}
        </Form>
    )

    return (
        renderForm()
    )
}
